/**
 * @file movements-filter.ts
 * @description Клиентская доводка окна журнала движений
 * (phase19.24 этап 1, шаг 2).
 *
 * Сервер отдаёт окно по складу, периоду и (если операция ложится на один вид)
 * по `kind`. Остальное — здесь: поиск по названию, артикулу и номеру
 * документа, а также операции из нескольких видов («перемещение»,
 * «корректировка»), которых серверный `kind` выразить не может.
 *
 * Логика вынесена из панели, чтобы «что попало в отбор» проверялось тестом, а
 * не глазами: итог «приход / расход / сальдо» считается ровно по этим строкам.
 */

import { formatSkuName, type StockMovementDto } from "./api-wms";
import {
  matchesMovementOperation,
  type MovementOperation,
} from "./movements-texts";

/**
 * Поиск по загруженному окну: название товара, артикул (снимок и текущий) и
 * номер документа-основания.
 *
 * Поиск клиентский осознанно: сервер ищет по `skuId`, а кладовщик вводит
 * «GR-2026» или кусок названия. Окно ограничено лимитом, поэтому экран рядом с
 * результатом показывает «Показано N из M» и подсказку сузить период.
 */
export function matchesMovementSearch(
  movement: StockMovementDto,
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    formatSkuName(movement.sku),
    movement.skuCode,
    movement.sku?.code,
    movement.document?.number,
  ];
  return haystack.some((part) => part?.toLowerCase().includes(needle));
}

/**
 * Движения одной партии: номер сверяем по вхождению, как в остатках.
 *
 * Пункт «Движения по партии» из «⋯» строки остатка приводит сюда со ссылкой
 * `?view=movements&lot=L-240517` (phase19.24 этап 1, шаг 4): без этого
 * фильтра ссылка открывала бы весь журнал склада.
 */
export function matchesMovementLot(
  movement: StockMovementDto,
  lot: string,
): boolean {
  const needle = lot.trim().toLowerCase();
  if (!needle) return true;
  return (movement.lot?.batchNumber ?? "").toLowerCase().includes(needle);
}

/** Номер партии строки в нижнем регистре; без партии — пусто. */
function lotKey(movement: StockMovementDto): string {
  return (movement.lot?.batchNumber ?? "").toLowerCase();
}

/**
 * Поиск журнала одной строкой (SC59): товар, артикул, документ и номер
 * партии — по вхождению. Но если строка поиска ЦЕЛИКОМ равна номеру партии
 * из окна, показываем только эту партию: «Движения по партии» из «⋯» строки
 * остатка подставляет номер в поиск, и «RCP-2026-000018-1» по вхождению
 * находила бы ещё «-10…-19» (ревью phase19.31).
 *
 * @param rows  строки, которые фильтруем (уже отобранные по операции);
 * @param known всё окно журнала — по нему решаем, что запрос — номер партии
 *              (отбор по операции мог убрать строки этой партии).
 */
export function searchMovements(
  rows: StockMovementDto[],
  query: string,
  known: StockMovementDto[] = rows,
): StockMovementDto[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return rows;
  if (known.some((movement) => lotKey(movement) === needle)) {
    return rows.filter((movement) => lotKey(movement) === needle);
  }
  return rows.filter((movement) => matchesMovementSearch(movement, query) || matchesMovementLot(movement, query));
}

/** Операция + поиск + партия одной функцией — источник правды для итога. */
export function filterMovements(
  items: StockMovementDto[],
  filter: { operation: MovementOperation; search: string; lot?: string },
): StockMovementDto[] {
  return items.filter(
    (movement) =>
      matchesMovementOperation(movement, filter.operation) &&
      matchesMovementSearch(movement, filter.search) &&
      matchesMovementLot(movement, filter.lot ?? ""),
  );
}
